import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Link, useLocation, useParams } from 'react-router-dom';

import {
  callMasterScopeAPI,
  callMGCMasterAPI,
  callStrategiesGetAPI,
  callUserInfoAPI
} from '../shared/APIHelper';
import { findUSIndicatorNew } from '../shared/UtilFunctions';
import { Box, Button, Typography } from '@mui/material';
import NoAccessEmployee from './NoAccessEmployee';
import { useAPIProgress } from '../shared/APIProgress';
import { useStatusMessages } from '../shared/StatusMessages';
import { useUserInfo } from '../shared/UserInfo';
import { getFormattedUserNameWithId } from '../shared/UserLogin';
import {
  prepareAllMGCSpendData,
  prepareStrategySpendData
} from './SpendChart/SpendUtils';
import { useDispatch } from 'react-redux';
import {
  setIsEdit,
  setSpendGraphData as setSpendGraphDataRedux
} from './Strategy/strategySlice';
import MessageBanner from '../shared/MessageBanner';
import {
  STRATEGY_STATUS_ACTIVE,
  STRATEGY_STATUS_DRAFT
} from '../shared/AppConstants';

import { useAdobeAnalytics } from '../utils/useAdobeAnalytics';

import { canEditTheStrategy, exportStrategyToCSV } from './StrategyUtils';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import {
  StrategyDetailsTabList,
  StrategyDetailsTabPanels
} from './StrategyDetailsView';
import { PhaseAndStatusBadge } from './Strategy/PhaseAndStatusBadge';
import { PageHeader } from '@deere/fuel-react/PageHeader.js';
import { PageHeaderActions } from '@deere/fuel-react/PageHeaderActions.js';
import { PageHeaderTitle } from '@deere/fuel-react/PageHeaderTitle.js';
import { PageContent } from '@deere/fuel-react/PageContent.js';

export default function ViewStrategy() {
  const strategicSupplierDataGridApiRef = useGridApiRef();
  const nonStrategicSupplierDataGridApiRef = useGridApiRef();
  const info = useUserInfo();
  const hasAccess = info?.hasAccess;
  const tokenAvailable = info?.token;
  const isAdmin = info?.isAdmin;
  const hasCreateAccess = info?.hasCreateAccess;
  const loggedInUser = info?.userId;
  const { pushAdobeAnalytics } = useAdobeAnalytics();
  const [tab, setTab] = useState(0);
  let Location = useLocation();
  const strategyInfo = Location.state?.strategy;
  const masterMGCSFromState = Location.state?.masterMGCs;

  const [masterMGCJson, setMasterMGCJson] = useState(null);
  const [masterScopeJson, setMasterScopeJson] = useState(null);
  const [scopeUSIndicatorMap, setScopeUSIndicatorMap] = useState(null);
  const [hasUSScope, setHasUSScope] = useState(null);

  const { strategyId } = useParams();
  const [strategyNumber] = useState(strategyId || 0);
  const { statusMessages, setStatusErrorMsg } = useStatusMessages();

  const [strategyInformation, setStrategyInformation] = useState(null);
  const [userInfoMap, setUserInfoMap] = useState(null);
  const [spendGraphData, setSpendGraphData] = useState(null);
  const dispatch = useDispatch();
  const [allMGCSpend, setAllMGCSpend] = useState(null);
  const { apiInProgressCount, incrementAPIInProgress, decrementAPIInProgress } =
    useAPIProgress();

  const commonProps = {
    tokenAvailable: tokenAvailable,
    setStatusErrorMsg: setStatusErrorMsg,
    incrementAPIInProgress: incrementAPIInProgress,
    decrementAPIInProgress: decrementAPIInProgress,
    strategyNumber: strategyNumber,
    masterScopeJson: masterScopeJson,
    apiInProgressCount: apiInProgressCount,
    isEdit: false
  };

  useEffect(() => {
    // Reset isEdit state when entering View mode to prevent stale state from Edit mode
    dispatch(setIsEdit(false));
  }, [dispatch]);

  useEffect(() => {
    pushAdobeAnalytics('View Strategy', info);
  }, [info]);

  useEffect(() => {
    if (tokenAvailable && strategyNumber > 0) {
      if (strategyInfo) setStrategyInformation(strategyInfo);
      else
        callStrategiesGetAPI(
          strategyNumber,
          incrementAPIInProgress,
          decrementAPIInProgress,
          setStatusErrorMsg,
          setStrategyInformation
        );

      if (masterMGCSFromState) setMasterMGCJson(masterMGCSFromState);
      else
        callMGCMasterAPI(
          setMasterMGCJson,
          incrementAPIInProgress,
          decrementAPIInProgress,
          setStatusErrorMsg,
          true
        );

      callMasterScopeAPI(
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        setMasterScopeJson,
        setScopeUSIndicatorMap
      );
    }
  }, [strategyNumber, tokenAvailable, masterMGCSFromState]);

  useEffect(() => {
    if (strategyInformation) {
      // Load Users iff null
      if (!userInfoMap) {
        let tmpUserList = [
          strategyInformation.ownerId,
          strategyInformation.approverId
        ];

        callUserInfoAPI(
          tmpUserList,
          setUserInfoMap,
          null,
          incrementAPIInProgress,
          decrementAPIInProgress,
          setStatusErrorMsg
        );
      }
      prepareStrategySpendData(
        strategyInformation.invoiceSpend,
        strategyInformation.forecastSpend,
        setSpendGraphData
      );
    }
  }, [strategyInformation]);

  useEffect(() => {
    if (spendGraphData) {
      dispatch(setSpendGraphDataRedux(spendGraphData));
    }
  }, [spendGraphData]);

  useEffect(() => {
    if (
      strategyInformation &&
      (strategyInformation.invoiceSpend || strategyInformation.forecastSpend)
    )
      prepareAllMGCSpendData(strategyInformation, setAllMGCSpend);
  }, [strategyInformation]);

  useEffect(() => {
    if (strategyInformation?.scope && scopeUSIndicatorMap) {
      let hasUsIndicator = findUSIndicatorNew(
        strategyInformation.scope,
        scopeUSIndicatorMap
      );
      setHasUSScope(hasUsIndicator);
    }
  }, [strategyInformation, scopeUSIndicatorMap]);

  function paintStrategyHeader() {
    return (
      <PageHeaderTitle>
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.fuel6Tokens.JddcSpacingMedium
          })}
        >
          <Typography variant="h1">Strategy #{strategyNumber}</Typography>
          <PhaseAndStatusBadge strategy={strategyInformation} />
        </Box>
        <Typography color="textSecondary">
          Last updated on{' '}
          {dayjs(strategyInformation.updatedOn).format('DD-MMM-YYYY')} by{' '}
          {getFormattedUserNameWithId(
            userInfoMap?.get(strategyInformation.updatedById),
            strategyInformation.updatedById
          )}
        </Typography>
      </PageHeaderTitle>
    );
  }

  function paintMaintainStrategyButton() {
    let uri;
    let label;

    if (
      canEditTheStrategy(
        strategyInformation,
        loggedInUser,
        isAdmin,
        hasCreateAccess
      )
    ) {
      if (strategyInformation.status === STRATEGY_STATUS_DRAFT) {
        uri = '/createStrategy/';
        label = 'Finish Creating Strategy';
      } else if (strategyInformation.status === STRATEGY_STATUS_ACTIVE) {
        uri = '/editStrategy/';
        label = 'Maintain Strategy';
      }

      return (
        <Link
          to={{
            pathname: uri + strategyId
          }}
        >
          <Button variant="primary">{label}</Button>
        </Link>
      );
    }
  }
  return !info ? (
    <></>
  ) : !hasAccess ||
    (!hasCreateAccess &&
      strategyInformation &&
      STRATEGY_STATUS_DRAFT === strategyInformation.status) ? (
    <NoAccessEmployee userId={loggedInUser} />
  ) : !strategyInformation && statusMessages && statusMessages.error ? (
    <MessageBanner
      statusMessages={statusMessages}
      apiInProgressCount={apiInProgressCount}
    />
  ) : (
    strategyInformation && (
      <>
        <PageHeader>
          {paintStrategyHeader()}
          <PageHeaderActions>
            <Button
              variant="tertiary"
              startIcon={<fuel-icon name="download" />}
              onClick={(e) =>
                exportStrategyToCSV(
                  e,
                  strategyInformation,
                  hasUSScope,
                  userInfoMap,
                  allMGCSpend,
                  strategicSupplierDataGridApiRef,
                  nonStrategicSupplierDataGridApiRef
                )
              }
            >
              Download XLSX
            </Button>
            {paintMaintainStrategyButton()}
          </PageHeaderActions>
          <Box sx={{ gridArea: 'tabs' }}>
            <MessageBanner
              statusMessages={statusMessages}
              apiInProgressCount={apiInProgressCount}
            />
            <StrategyDetailsTabList value={tab} onChange={setTab} />
          </Box>
        </PageHeader>
        <PageContent sx={{ minWidth: 0 }}>
          <StrategyDetailsTabPanels
            tabValue={tab}
            strategicSupplierDataGridApiRef={strategicSupplierDataGridApiRef}
            nonStrategicSupplierDataGridApiRef={
              nonStrategicSupplierDataGridApiRef
            }
            strategyInformation={strategyInformation}
            masterMGCJson={masterMGCJson}
            masterScopeJson={masterScopeJson}
            hasUSScope={hasUSScope}
            userInfoMap={userInfoMap}
            commonProps={commonProps}
            spendGraphData={spendGraphData}
            allMGCSpend={allMGCSpend}
            statusMessages={statusMessages}
            apiInProgressCount={apiInProgressCount}
            info={info}
          />
        </PageContent>
      </>
    )
  );
}
